import React, { memo, useEffect } from 'react'
import { FunctionComponent } from 'react'
import styled, { css, FlattenSimpleInterpolation, Keyframes, keyframes } from 'styled-components'
import Grid from '../Grid'
import { ProgressCircleProps } from './ProgressCircleProps'

type AnimatedCircleProps = React.SVGProps<SVGCircleElement> & {
    circumference: number
    animationDuration?: number
}

export const circleKeyframes = (circumference: number): Keyframes => keyframes`
    from { stroke-dashoffset: ${circumference} }
    to  { stroke-dashoffset: 0 }
`

export const AnimatedCircle = styled.circle<AnimatedCircleProps>`
    animation: ${(props): FlattenSimpleInterpolation | string =>
        props.animationDuration
            ? css`
                  ${circleKeyframes(props.circumference)} ${props.animationDuration / 1000}s linear
              `
            : ''};
    }
`

const ProgressCircle: FunctionComponent<ProgressCircleProps> = ({
    testID,
    size,
    backgroundColor,
    fillColor,
    strokeWidth,
    animationDuration = 3000,
    onAnimationEnd,
    children,
}) => {
    const radius = (size - strokeWidth) / 2
    const circumference = radius * Math.PI * 2
    const transform = `rotate(275 ${radius} ${radius})`
    const viewBox = `${-strokeWidth} ${-strokeWidth} ${size + strokeWidth} ${size + strokeWidth}`
    const [animDuration, setAnimDuration] = React.useState<number | undefined>(undefined)
    useEffect(() => {
        // animation should start after mount
        setAnimDuration(animationDuration)
    }, [animationDuration])
    return (
        <>
            <svg width={size} height={size} viewBox={viewBox}>
                <circle
                    stroke={backgroundColor}
                    cx={radius}
                    cy={radius}
                    r={radius}
                    strokeWidth={strokeWidth}
                    fill="none"
                />
                <AnimatedCircle
                    data-testid={testID}
                    stroke={fillColor}
                    transform={transform}
                    cx={radius}
                    cy={radius}
                    r={radius}
                    strokeDasharray={circumference}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    fill="none"
                    circumference={circumference}
                    animationDuration={animDuration}
                    onAnimationEnd={onAnimationEnd}
                />
            </svg>
            {children ? (
                <Grid position="absolute" width={size} height={size} center>
                    {children}
                </Grid>
            ) : null}
        </>
    )
}

export default memo(ProgressCircle)
